import {useNavigate, useParams} from "react-router-dom";
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {AgGridReact} from "ag-grid-react";
import {LocalizationAgGrid} from "../../../utils/localization-ag-grid";
import {useQuery} from "@tanstack/react-query";
import {toast} from "react-toastify";
import {AlertMessage} from "../../../utils/alert-message";
import {FaPlus} from "react-icons/fa";

import TemplateDefault from "../../../components/template-default";
import Button from "../../../components/button";
import InputForm from "../../../components/input-form";
import Card from "../../../components/card";
import InputText from "../../../components/input-text";
import templatesService from "../../../services/templates-service";
import MySwal from "sweetalert2";
import RemoveButtonCellRenderer from "../../../components/cell-renderer/remove-button-cell-renderer";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css"


const initialData = {
    name: "",
    description: "",
    items: []
}

const TemplateForm = () => {
    const {id} = useParams();
    const navigate = useNavigate();

    const itemsRef = useRef();
    const [itemDescription, setItemDescription] = useState("");
    const [items, setItems] = useState([]);
    const [data, setData] = useState(initialData);
    const [isLoading, setIsLoading] = useState(false);

    const defaultColDef = useMemo(() => ({resizable: false}), []);

    const [columnDefs] = useState([
        {headerName: "Item", field: "item_description", flex: 1, minWidth: 250, sortable: false, editable: true, rowDrag: true, suppressMoveWhenRowDragging: true},
        {
            field: "",
            sortable: false,
            maxWidth: 70,
            editable: false,
            cellRenderer: RemoveButtonCellRenderer,
            flex: 2
        },
    ]);

    useEffect(() => {
        document.title = "SISTEMA INTERNO";
    }, []);

    const {isFetching, refetch} = useQuery({
        queryFn: () => templatesService.getById(id).then((data) => {
            setItems(data.items);
            setData(data)
            return data;
        }),
        queryKey: ['templates'],
        enabled: !!id,
        initialData
    });

    const submit = () => {
        const formData = new FormData();
        Object.keys(data).forEach(key => {
            formData.append(key, data[key]);
        });

        if (!!id) formData.append('_method', 'PUT');

        const itemsRow = [];
        itemsRef.current?.api.forEachNode((node) => itemsRow.push(node.data));

        const jsonItems = JSON.stringify(itemsRow);
        formData.append("items", jsonItems);

        const response = !!id ? templatesService.edit(formData, id) : templatesService.create(formData);

        response
            .then(({data}) => {
                id ? refetch() : navigate(`/templates/form/${data.id}`);

                toast.success("Salvo com sucesso.")
            })
            .catch((err) => {
                const {data: {message}} = err.response;
                toast.error(AlertMessage[message] ?? "Não foi possível salvar");
            });
    }

    const deleteTemplate = () => {
        MySwal.fire({
            icon: "warning",
            title: "Excluir!",
            showCancelButton: true,
            text: "Tem certeza que deseja excluir o template?",
            confirmButtonText: "Sim",
            cancelButtonText: "Não",
            reverseButtons: true
        }).then(({isConfirmed}) => {
            if (isConfirmed) {
                setIsLoading(true);

                templatesService.delete(id)
                    .then(() => {
                        navigate("/templates");
                        toast.success("Excluído com sucesso.");
                    })
                    .catch((err) => {
                        const {data: {message}} = err.response;
                        toast.error(AlertMessage[message] ?? "Não foi possível excluir");
                    })
                    .finally(() => setIsLoading(false));
            }
        });
    }

    const handleInput = ({target: {id, value}}) => setData(prev => ({...prev, [id]: value}));

    const addItem = useCallback(() => {
        if (!itemDescription) {
            toast.error(AlertMessage.generalEmpty);
            return
        }

        itemsRef.current?.api.applyTransaction({add: [{item_description: itemDescription}]});
        setItemDescription("");
    }, [itemsRef, itemDescription]);

    const gridOptions = {
        localeText: LocalizationAgGrid,
        rowDragManaged: true,
        animateRows: true,
    };

    return (
        <TemplateDefault title={"Cadastro de template" + (id ? ` #${id}` : "")} isLoading={isLoading || isFetching}>

            <Card title="Dados">
                <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-05">
                    <InputForm onChange={handleInput} id="name" name="name" label="Nome *" value={data.name} col="col-span-12"/>
                    <InputText
                        onChange={handleInput}
                        id="description"
                        name="description"
                        label="Descrição *"
                        height={"h-[calc(100vh-653px)] min-h-[150px]"}
                        col="col-span-12"
                        value={data.description}
                    />
                </div>
            </Card>

            <Card title="Checklist" wrapperClass="mt-5">
                <div className="flex flex-col lg:grid grid-cols-1 gap-x-6 gap-y-4 lg:grid-cols-12">
                    <InputForm id="item_description" label="Item" col="col-span-10" value={itemDescription}
                               onChange={({target: {value}}) => setItemDescription(value)}/>
                    <Button onClick={addItem} target="primary" col="col-span-2">
                        <FaPlus/>
                    </Button>
                </div>

                <div className={"ag-theme-quartz grid mt-4"} style={{ height: "200px" }}>
                    <AgGridReact
                        ref={itemsRef}
                        rowHeight={32}
                        rowData={items}
                        columnDefs={columnDefs}
                        defaultColDef={defaultColDef}
                        suppressRowClickSelection={true}
                        singleClickEdit={true}
                        stopEditingWhenCellsLoseFocus={true}
                        gridOptions={gridOptions}
                    />
                </div>
            </Card>

            <Card wrapperClass="mt-5">
                <div className="flex justify-end gap-3">
                    {
                        id &&
                        <Button onClick={deleteTemplate} target="danger">
                            Excluir
                        </Button>
                    }
                    <Button onClick={() => navigate("/templates")} target="warning">
                        Cancelar
                    </Button>
                    <Button type="submit" target="success" onClick={submit}>
                        Salvar
                    </Button>
                </div>
            </Card>
        </TemplateDefault>
    );
};

export default TemplateForm;